import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from './components/Modal';

const TaxAssessmentModal = ({ isOpen, onClose, propertyId, assessment = null, onSave }) => {
  const [formData, setFormData] = useState({
    assessment_year: new Date().getFullYear(),
    tax_statement_year: '',
    land_assessed_value: 0,
    land_taxable_value: 0,
    buildings_assessed_value: 0,
    buildings_taxable_value: 0,
    dwelling_assessed_value: 0,
    dwelling_taxable_value: 0,
    total_assessed_value: 0,
    total_taxable_value: 0,
    levy_rate: 0,
    county_url: '',
    exemptions: [],
    credits: []
  });

  const [suggestions, setSuggestions] = useState({ exemption_names: [], credit_names: [] });

  const formatWithCommas = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const parts = val.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const parseCommas = (val) => {
    return val.replace(/,/g, '');
  };

  useEffect(() => {
    if (assessment) {
      setFormData({
        ...assessment,
        levy_rate: parseFloat(assessment.levy_rate) || 0
      });
    } else {
      setFormData({
        assessment_year: new Date().getFullYear(),
        tax_statement_year: '',
        land_assessed_value: 0,
        land_taxable_value: 0,
        buildings_assessed_value: 0,
        buildings_taxable_value: 0,
        dwelling_assessed_value: 0,
        dwelling_taxable_value: 0,
        total_assessed_value: 0,
        total_taxable_value: 0,
        levy_rate: 0,
        county_url: '',
        exemptions: [],
        credits: []
      });
    }

    // Fetch suggestions
    axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/tax-assessment-suggestions/`)
      .then(res => setSuggestions(res.data))
      .catch(err => console.error("Error fetching suggestions:", err));
  }, [assessment, isOpen]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let finalValue = value;
    if (type === 'text' && (name.includes('value') || name.includes('amount') || name === 'levy_rate')) {
       finalValue = parseCommas(value);
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleNestedChange = (type, index, field, value) => {
    const newList = [...formData[type]];
    let finalValue = value;
    if (field === 'amount') {
      finalValue = parseCommas(value);
    }
    newList[index][field] = finalValue;
    setFormData(prev => ({ ...prev, [type]: newList }));
  };

  const addNestedRow = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], { name: '', amount: 0 }]
    }));
  };

  const removeNestedRow = (type, index) => {
    const newList = [...formData[type]];
    newList.splice(index, 1);
    setFormData(prev => ({ ...prev, [type]: newList }));
  };

  // Calculations
  const totalAssessedValue = (parseFloat(formData.land_assessed_value) || 0) + (parseFloat(formData.buildings_assessed_value) || 0) + (parseFloat(formData.dwelling_assessed_value) || 0);
  const totalTaxableValue = (parseFloat(formData.land_taxable_value) || 0) + (parseFloat(formData.buildings_taxable_value) || 0) + (parseFloat(formData.dwelling_taxable_value) || 0);
  const totalExemptions = formData.exemptions.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const netTaxableValue = totalTaxableValue - totalExemptions;
  const grossTax = (netTaxableValue * (parseFloat(formData.levy_rate) || 0)) / 1000;
  const totalCredits = formData.credits.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
  const netAnnualTaxes = Math.round((grossTax - totalCredits) * 100) / 100;

  // Rollback Rate Calculations
  const landRollbackRate = parseFloat(formData.land_assessed_value) ? (parseFloat(formData.land_taxable_value) || 0) / parseFloat(formData.land_assessed_value) : 0;
  const buildingsRollbackRate = parseFloat(formData.buildings_assessed_value) ? (parseFloat(formData.buildings_taxable_value) || 0) / parseFloat(formData.buildings_assessed_value) : 0;
  const dwellingRollbackRate = parseFloat(formData.dwelling_assessed_value) ? (parseFloat(formData.dwelling_taxable_value) || 0) / parseFloat(formData.dwelling_assessed_value) : 0;
  const totalRollbackRate = totalAssessedValue ? totalTaxableValue / totalAssessedValue : 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      total_assessed_value: totalAssessedValue,
      total_taxable_value: totalTaxableValue,
      net_taxable_value: netTaxableValue,
      gross_tax: grossTax,
      net_annual_taxes: netAnnualTaxes,
      land_rollback_rate: landRollbackRate,
      buildings_rollback_rate: buildingsRollbackRate,
      dwelling_rollback_rate: dwellingRollbackRate,
      total_rollback_rate: totalRollbackRate
    };

    const url = assessment 
      ? `/api/balance_sheet/assets/physical_assets/resi_properties/update-tax-assessment/${assessment.id}/`
      : `/api/balance_sheet/assets/physical_assets/resi_properties/${propertyId}/add-tax-assessment/`;
    
    if (assessment) {
        axios.patch(url, payload)
            .then(res => {
                onSave(res.data);
                onClose();
            })
            .catch(err => alert(err.response?.data?.detail || err.message));
    } else {
        axios.post(url, payload)
            .then(res => {
                onSave(res.data);
                onClose();
            })
            .catch(err => alert(err.response?.data?.detail || err.message));
    }
  };

  const inputStyle = {
    background: '#1f2937',
    border: '1px solid #374151',
    color: '#e5e7eb',
    borderRadius: 4,
    padding: '4px 8px',
    width: '100%'
  };

  const labelStyle = { display: 'block', fontSize: '0.875rem', marginBottom: 4, color: '#9ca3af' };
  const currencyInputContainerStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
  const dollarSignStyle = { position: 'absolute', left: 8, color: '#9ca3af' };
  const currencyInputStyle = { ...inputStyle, paddingLeft: 20 };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={assessment ? "Edit Tax Assessment" : "Add Tax Assessment"} width={800}>
      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Assessment Year</label>
            <input type="number" name="assessment_year" value={formData.assessment_year} onChange={handleChange} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle}>Tax Statement Year (e.g. 2025/2026)</label>
            <input type="text" name="tax_statement_year" value={formData.tax_statement_year} onChange={handleChange} style={inputStyle} />
          </div>
        </div>

        <div style={{ border: '1px solid #374151', padding: 12, borderRadius: 8 }}>
          <div style={{ fontWeight: 600, borderBottom: '1px solid #374151', paddingBottom: 4, marginBottom: 8 }}>Values</div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Category</div>
            <div style={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Assessed Value</div>
            <div style={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Taxable Value</div>
            <div style={{ fontWeight: 600, color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase', textAlign: 'right' }}>Rollback</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: '#e5e7eb' }}>Land</div>
            <div style={currencyInputContainerStyle}>
              <span style={dollarSignStyle}>$</span>
              <input type="text" name="land_assessed_value" value={formatWithCommas(formData.land_assessed_value)} onChange={handleChange} style={currencyInputStyle} />
            </div>
            <div style={currencyInputContainerStyle}>
              <span style={dollarSignStyle}>$</span>
              <input type="text" name="land_taxable_value" value={formatWithCommas(formData.land_taxable_value)} onChange={handleChange} style={currencyInputStyle} />
            </div>
            <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '0.875rem' }}>
              {landRollbackRate.toFixed(6)}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: '#e5e7eb' }}>Buildings</div>
            <div style={currencyInputContainerStyle}>
              <span style={dollarSignStyle}>$</span>
              <input type="text" name="buildings_assessed_value" value={formatWithCommas(formData.buildings_assessed_value)} onChange={handleChange} style={currencyInputStyle} />
            </div>
            <div style={currencyInputContainerStyle}>
              <span style={dollarSignStyle}>$</span>
              <input type="text" name="buildings_taxable_value" value={formatWithCommas(formData.buildings_taxable_value)} onChange={handleChange} style={currencyInputStyle} />
            </div>
            <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '0.875rem' }}>
              {buildingsRollbackRate.toFixed(6)}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <div style={{ color: '#e5e7eb' }}>Dwelling</div>
            <div style={currencyInputContainerStyle}>
              <span style={dollarSignStyle}>$</span>
              <input type="text" name="dwelling_assessed_value" value={formatWithCommas(formData.dwelling_assessed_value)} onChange={handleChange} style={currencyInputStyle} />
            </div>
            <div style={currencyInputContainerStyle}>
              <span style={dollarSignStyle}>$</span>
              <input type="text" name="dwelling_taxable_value" value={formatWithCommas(formData.dwelling_taxable_value)} onChange={handleChange} style={currencyInputStyle} />
            </div>
            <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '0.875rem' }}>
              {dwellingRollbackRate.toFixed(6)}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 100px', gap: 12, alignItems: 'center', paddingTop: 8, borderTop: '1px solid #374151' }}>
            <div style={{ fontWeight: 700, color: '#e5e7eb' }}>Total Values</div>
            <div style={{ ...inputStyle, background: '#111827', fontWeight: 700 }}>
              ${totalAssessedValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ ...inputStyle, background: '#111827', fontWeight: 700 }}>
              ${totalTaxableValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ textAlign: 'right', fontWeight: 700, color: '#10b981' }}>
              {totalRollbackRate.toFixed(6)}
            </div>
          </div>
        </div>

        <div style={{ border: '1px solid #374151', padding: 12, borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Exemptions</span>
            <button type="button" onClick={() => addNestedRow('exemptions')} className="btn btn-xs btn-outline-primary">+ Add</button>
          </div>
          {formData.exemptions.map((ex, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <input 
                  list="exemption-names" 
                  value={ex.name} 
                  onChange={(e) => handleNestedChange('exemptions', idx, 'name', e.target.value)} 
                  placeholder="Name" 
                  style={inputStyle} 
                />
                <datalist id="exemption-names">
                  {suggestions.exemption_names.map(n => <option key={n} value={n} />)}
                </datalist>
              </div>
              <div style={{ ...currencyInputContainerStyle, width: 120 }}>
                <span style={dollarSignStyle}>$</span>
                <input 
                  type="text" 
                  value={formatWithCommas(ex.amount)} 
                  onChange={(e) => handleNestedChange('exemptions', idx, 'amount', e.target.value)} 
                  placeholder="Amount" 
                  style={currencyInputStyle} 
                />
              </div>
              <button type="button" onClick={() => removeNestedRow('exemptions', idx)} style={{ color: '#ef4444', background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
          ))}
          <div style={{ textAlign: 'right', fontSize: '0.875rem', color: '#9ca3af' }}>Total Exemptions: ${totalExemptions.toLocaleString()}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Net Taxable Value</label>
            <div style={{ ...inputStyle, background: '#111827' }}>${netTaxableValue.toLocaleString()}</div>
          </div>
          <div>
            <label style={labelStyle}>Levy Rate (7+ decimals)</label>
            <input type="text" name="levy_rate" value={formatWithCommas(formData.levy_rate)} onChange={handleChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Gross Tax</label>
            <div style={{ ...inputStyle, background: '#111827' }}>${grossTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div style={{ border: '1px solid #374151', padding: 12, borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Credits</span>
            <button type="button" onClick={() => addNestedRow('credits')} className="btn btn-xs btn-outline-primary">+ Add</button>
          </div>
          {formData.credits.map((cr, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <input 
                  list="credit-names" 
                  value={cr.name} 
                  onChange={(e) => handleNestedChange('credits', idx, 'name', e.target.value)} 
                  placeholder="Name" 
                  style={inputStyle} 
                />
                <datalist id="credit-names">
                  {suggestions.credit_names.map(n => <option key={n} value={n} />)}
                </datalist>
              </div>
              <div style={{ ...currencyInputContainerStyle, width: 120 }}>
                <span style={dollarSignStyle}>$</span>
                <input 
                  type="text" 
                  value={formatWithCommas(cr.amount)} 
                  onChange={(e) => handleNestedChange('credits', idx, 'amount', e.target.value)} 
                  placeholder="Amount" 
                  style={currencyInputStyle} 
                />
              </div>
              <button type="button" onClick={() => removeNestedRow('credits', idx)} style={{ color: '#ef4444', background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
          ))}
          <div style={{ textAlign: 'right', fontSize: '0.875rem', color: '#9ca3af' }}>Total Credits: ${totalCredits.toLocaleString()}</div>
        </div>

        <div style={{ border: '1px solid #374151', padding: 12, borderRadius: 8 }}>
          <label style={labelStyle}>County Assessor URL</label>
          <input 
            type="url" 
            name="county_url" 
            value={formData.county_url || ''} 
            onChange={handleChange} 
            placeholder="https://county-assessor-page.com/property/123" 
            style={inputStyle} 
          />
        </div>

        <div style={{ textAlign: 'right', borderTop: '1px solid #374151', paddingTop: 12 }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981', marginBottom: 12 }}>
            Net Annual Taxes: ${netAnnualTaxes.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} className="btn btn-outline-secondary">Cancel</button>
            <button type="submit" className="btn btn-primary">Save Assessment</button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default TaxAssessmentModal;
